import { ChangeEvent, useState } from 'react'
import * as yup from 'yup'
import cn from 'classnames'
import { useToasts } from 'react-toast-notifications'
import { Formik, Form, FormikHelpers } from 'formik-latest'

import TextArea from './TextArea'
import TextInput from './TextInput'

import ActionButton from 'backoffice/components/DropRequests/ActionButton'
import { usePutPetitionQuery } from 'backoffice/components/DropRequests/usePetitionsQuery'

import { Actions, Status } from 'backoffice/components/DropRequests/types/types'

import styles from './ActionForm.module.scss'

interface Values {
  id: string
  status: Status
  comment: string
  amount?: number
}

const FormCustom = (props: any) => {
  const { isSubmitting, handleChange, isValid, action, maxCommentLength, requestedAmount, submitForm, isMutating } = props

  const actionsMap = [
    {
      action: Actions.APPROVE,
      params: {
        submitButtonClass: styles.actionApprove,
        label: 'Approve Drop',
        isAmountFieldEnabled: true,
      },
    },
    {
      action: Actions.FLAG,
      params: {
        submitButtonClass: styles.actionFlag,
        label: 'Flag Drop',
        isAmountFieldEnabled: false,
      },
    },
    {
      action: Actions.REJECT,
      params: {
        submitButtonClass: styles.actionReject,
        label: 'Reject Drop',
        isAmountFieldEnabled: false,
      },
    },
  ]

  const actionParams = actionsMap.find((item) => item.action === action)?.params

  const submitClassname = `submit ${actionParams?.submitButtonClass}`

  const isAmountFieldEnabled = actionParams?.isAmountFieldEnabled
  const isDisabledSubmit = isSubmitting || isMutating || !isValid
  const [commentCharacterCounter, setCommentCounterCharacter] = useState(0)

  const onChangeCommentHandler = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (e) {
      setCommentCounterCharacter(e.target.value.length)
      handleChange(e)
    }
  }

  return (
    <Form className={styles.actionForm}>
      {isAmountFieldEnabled && (
        <>
          <label className={styles.label} htmlFor="amount">
            Mint links
          </label>
          {/* @ts-ignore TODO: remove ignore TS validation */}
          <TextInput id="amount" name="amount" type="number" min="1" className={styles.input}>
            <span className={styles.totalAmmount}>of {requestedAmount} mint links requested</span>
          </TextInput>
        </>
      )}

      <label className={styles.label} htmlFor="comment">
        Comment (optional)
      </label>
      <TextArea
        id="comment"
        name="comment"
        className={styles.textArea}
        placeholder="insert comment"
        rows={1}
        maxLength={maxCommentLength}
        onChange={onChangeCommentHandler}
      />
      <span className={styles.commentCharacterCounter}>{commentCharacterCounter}/500</span>

      {/* @ts-ignore TODO: remove ignore TS validation */}
      <ActionButton
        className={cn(styles.submitButton, submitClassname, { [styles.disabled]: isDisabledSubmit })}
        isDisabled={isDisabledSubmit}
        onClick={() => submitForm}
        type="submit"
      >
        {actionParams?.label}
      </ActionButton>
    </Form>
  )
}

// TODO: refactor this mapper, I think we don't need the action Enum, we should only have the status enum
const statusMapper = (action: Actions): Status => {
  if (action === Actions.APPROVE) return Status.APPROVED
  if (action === Actions.REJECT) return Status.REJECTED
  if (action === Actions.FLAG) return Status.FLAGGED

  return Status.PENDING
}

const ActionForm = ({ action, requestedAmount, id, handleClose }: any) => {
  const mutation = usePutPetitionQuery()
  const { addToast } = useToasts()

  const REQUESTED_AMOUNT = requestedAmount
  const MAX_COMMENT_LENGHT = 500
  const hasAmount = action === Actions.APPROVE

  const validationSchema = yup.object({
    ...(hasAmount && {
      amount: yup
        .number()
        .integer()
        .min(1, 'This value must be greater than 0')
        .max(REQUESTED_AMOUNT, `This value must be equal to or less than the requested (${REQUESTED_AMOUNT})`)
        .required('This field is required'),
    }),
    comment: yup
      .string()
      .max(MAX_COMMENT_LENGHT, `This field can contain a maximum of ${MAX_COMMENT_LENGHT} characters"`),
  })

  const initialValues = {
    id,
    status: statusMapper(action),
    comment: '',
    ...(hasAmount && { amount: requestedAmount }),
  }

  const onSubmitHandler = async (values: any, { setSubmitting }: FormikHelpers<Values>) => {
    try {
      await mutation.mutateAsync(values)
    } catch {
      addToast('There was a problem saving the changes', { appearance: 'error', autoDismiss: true })
    }

    setSubmitting(false)
    handleClose()
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmitHandler}
      validateOnBlur
      validateOnChange
    >
      {(props) => (
        <FormCustom
          {...props}
          action={action}
          requestedAmount={requestedAmount}
          maxCommentLength={MAX_COMMENT_LENGHT}
          isMutating={mutation.isLoading}
        />
      )}
    </Formik>
  )
}

export default ActionForm
